// =====================================================================================
// FILE: src/pages/Operator/OperatorOverview.jsx
// =====================================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { OPERATOR_BASES } from '../../config/operatorBases';
import { fetchOperatorRecords } from '../../services/operatorAirtable';

const THEME = {
  bg: '#0a0a0f',
  bgCard: '#141420',
  bgCardInner: '#1c1c2e',
  bgHover: '#22223a',
  border: 'rgba(255,255,255,0.06)',
  borderLight: 'rgba(255,255,255,0.10)',
  textPrimary: '#f8fafc',
  textSecondary: '#e2e8f0',
  textBody: '#cbd5e1',
  textMuted: '#94a3b8',
  accent: '#00c9a7',
};

const CONTENT_TYPES = [
  { key: 'cg', label: 'CG / Graphic Assets', shortLabel: 'CG / Graphics', color: '#3b82f6', icon: '🖥️' },
  { key: 'ribbon', label: 'LED Ribbon Assets', shortLabel: 'LED Ribbon', color: '#f59e0b', icon: '💡' },
  { key: 'iptv', label: 'IPTV Assets', shortLabel: 'IPTV', color: '#8b5cf6', icon: '📺' },
];

function computeStats(records) {
  const total = records.length;
  const built = records.filter(r => r.fields?.['Built at Venue']).length;
  const sent = records.filter(r => r.fields?.['Send To Watch Folder']).length;
  const needsBuild = records.filter(r => r.fields?.['Send To Watch Folder'] && !r.fields?.['Built at Venue']).length;
  const rejected = records.filter(r => r.fields?.['Content Status'] === 'Rejected').length;
  const unlocked = records.filter(r => r.fields?.['Content Status'] === 'Content Unlocked').length;
  const unchecked = records.filter(r => {
    const s = r.fields?.['Content Status'] || '';
    return !r.fields?.['Send To Watch Folder'] && !r.fields?.['Built at Venue']
      && s !== 'Rejected' && s !== 'Content Unlocked';
  }).length;
  return { total, built, sent, needsBuild, rejected, unlocked, unchecked };
}

// ─── Collapsible Tips Panel ───
function OperatorTips() {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      marginBottom: 14,
      background: THEME.bgCard,
      borderRadius: 10,
      border: `1px solid ${THEME.border}`,
      overflow: 'hidden',
    }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 18px', cursor: 'pointer', userSelect: 'none',
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = THEME.bgHover}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        <span style={{ fontSize: 14 }}>💡</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: THEME.textSecondary }}>Operator Tips</span>
        <span style={{ fontSize: 10, color: THEME.textMuted, transform: open ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.2s', display: 'inline-block' }}>▶</span>
      </div>
      {open && (
        <div style={{ padding: '12px 18px 18px', fontSize: 13, color: THEME.textBody }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: THEME.accent, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 14 }}>
            Getting Started — Operator Workflow
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { n: '1', color: '#60a5fa', bg: 'rgba(96,165,250,0.15)', title: 'Check & Ingest', desc: <>Review each content row. Click <b style={{ color: '#60a5fa' }}>Check & Ingest</b> to QA the asset and send it to the encoder watch folder. The row turns blue once checked.</> },
              { n: '2', color: '#22c55e', bg: 'rgba(34,197,94,0.15)', title: 'Built by Operator', desc: <>Once built in XPression/playout, tick <b style={{ color: '#22c55e' }}>Built by Operator</b>. This locks the row green — the club can no longer edit it.</> },
              { n: '3', color: '#f87171', bg: 'rgba(239,68,68,0.15)', title: 'Reject / Actions Menu', desc: <>Click <b style={{ color: '#22c55e' }}>Actions</b> to reject content, add comments, or unlock built rows. Use "Return to review" to undo.</> },
            ].map(s => (
              <div key={s.n} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 18, lineHeight: 1.2, flexShrink: 0, width: 28, textAlign: 'center', background: s.bg, borderRadius: 6, padding: '2px 0' }}>{s.n}</span>
                <div>
                  <div style={{ fontWeight: 700, color: s.color, marginBottom: 3 }}>{s.title}</div>
                  <div style={{ color: THEME.textBody, lineHeight: 1.6 }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${THEME.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Quick Reference</div>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', color: THEME.textBody }}>
              <span>📋 Click <b style={{ color: THEME.accent }}>Lumen8 ID</b> to copy</span>
              <span>✏️ <b style={{ color: THEME.accent }}>Take ID</b> — playout take number</span>
              <span>💬 <b style={{ color: '#60a5fa' }}>Actions → Comment</b> — feedback</span>
            </div>
          </div>
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${THEME.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Row Colours</div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', color: THEME.textBody }}>
              {[
                { bg: 'rgba(251,191,36,0.3)', b: '#fbbf24', l: 'Needs Check & Ingest' },
                { bg: 'rgba(96,165,250,0.3)', b: '#60a5fa', l: 'Checked — waiting build' },
                { bg: 'rgba(52,211,153,0.3)', b: '#34d399', l: 'Built & locked' },
                { bg: 'rgba(239,68,68,0.3)', b: '#f87171', l: 'Rejected' },
                { bg: 'rgba(192,132,252,0.3)', b: '#c084fc', l: 'Club updated' },
              ].map(c => (
                <span key={c.l} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: c.bg, border: `2px solid ${c.b}` }} />
                  <span>{c.l}</span>
                </span>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${THEME.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>⌨️ Shortcuts <span style={{ fontWeight: 400, textTransform: 'none' }}>(with rows selected)</span></div>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', color: THEME.textBody }}>
              {[['A', 'Approve'], ['R', 'Reject'], ['T', 'Ready for Testing'], ['Esc', 'Clear']].map(([k, l]) => (
                <span key={k}><code style={{ background: THEME.bgHover, padding: '2px 7px', borderRadius: 4, fontWeight: 700, color: '#e2e8f0', fontSize: 12 }}>{k}</code> {l}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Build attention sentence ───
function buildAttentionSentence(stats) {
  const parts = [];
  if (stats.needsBuild > 0) parts.push(`${stats.needsBuild} need${stats.needsBuild === 1 ? 's' : ''} to be built by operator`);
  if (stats.rejected > 0) parts.push(`${stats.rejected} Rejected`);
  if (stats.unchecked > 0) parts.push(`${stats.unchecked} Need QA Checking`);
  if (stats.unlocked > 0) parts.push(`${stats.unlocked} Updated by Club`);
  return parts;
}

// ─── Main Component ───
export default function OperatorOverview({ selectedClub, selectedView, onNavigate }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const pollRef = useRef(null);
  const loadingRef = useRef(false);

  const clubs = React.useMemo(() =>
    selectedClub === 'all'
      ? OPERATOR_BASES
      : OPERATOR_BASES.filter(b => b.id === selectedClub),
    [selectedClub]
  );

  const loadData = useCallback(async (showLoading = true) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (showLoading) setLoading(true);

    const results = await Promise.all(CONTENT_TYPES.map(async (ct) => {
      try {
        let allRecords = [];
        for (const club of clubs) {
          try {
            const records = await fetchOperatorRecords(club.id, ct.key, selectedView);
            allRecords.push(...records);
          } catch (e) { /* skip */ }
        }
        const filtered = allRecords.filter(r => {
          const name = (r.fields?.['Name'] || '').toLowerCase();
          const status = r.fields?.['Content Status'] || '';
          return !name.includes('example') && !name.includes('test player') && status !== 'Example Content';
        });
        return { key: ct.key, records: filtered, stats: computeStats(filtered) };
      } catch (e) {
        return { key: ct.key, records: [], stats: computeStats([]) };
      }
    }));

    const result = {};
    results.forEach(r => { result[r.key] = r; });
    setData(result);
    if (showLoading) setLoading(false);
    loadingRef.current = false;
  }, [clubs, selectedView]);

  const loadDataRef = useRef(loadData);
  loadDataRef.current = loadData;

  useEffect(() => {
    if (selectedView) loadData();
  }, [selectedView, selectedClub]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      if (selectedView) loadDataRef.current(false);
    }, 15000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedView]);

  const gt = (f) => CONTENT_TYPES.reduce((s, ct) => s + (data[ct.key]?.stats?.[f] || 0), 0);
  const grandTotal = gt('total');
  const grandBuilt = gt('built');
  const grandSent = gt('sent');
  const grandNeedsBuild = gt('needsBuild');
  const grandUnchecked = gt('unchecked');
  const grandRejected = gt('rejected');
  const grandUnlocked = gt('unlocked');
  const pctBuilt = grandTotal > 0 ? Math.round((grandBuilt / grandTotal) * 100) : 0;
  const pctSent = grandTotal > 0 ? Math.round((grandSent / grandTotal) * 100) : 0;

  const clubLabel = selectedClub === 'all' ? 'All Clubs' : (clubs[0]?.name || selectedClub);
  const matchLabel = selectedView || 'No match selected';
  const hasIssues = grandUnchecked > 0 || grandRejected > 0 || grandNeedsBuild > 0 || grandUnlocked > 0;

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: THEME.textPrimary, margin: 0 }}>Operator Dashboard</h2>
        <p style={{ fontSize: 13, color: THEME.textSecondary, margin: '4px 0 0' }}>Cross-content progress overview — {clubLabel}</p>
      </div>

      <OperatorTips />

      {/* No match */}
      {!selectedView && (
        <div style={{ padding: '40px 24px', textAlign: 'center', background: THEME.bgCard, borderRadius: 12, border: `1px solid ${THEME.border}` }}>
          <span style={{ fontSize: 32 }}>📋</span>
          <div style={{ fontSize: 15, fontWeight: 700, color: THEME.textPrimary, marginTop: 12 }}>Select a match to view progress</div>
          <div style={{ fontSize: 13, color: THEME.textSecondary, marginTop: 6 }}>Choose a match from the dropdown above</div>
        </div>
      )}

      {/* Loading */}
      {selectedView && loading && (
        <div style={{ padding: '40px 24px', textAlign: 'center', background: THEME.bgCard, borderRadius: 12, border: `1px solid ${THEME.border}` }}>
          <div style={{ fontSize: 14, color: THEME.textSecondary }}>Loading stats across CG, Ribbon, IPTV...</div>
        </div>
      )}

      {/* ── Dashboard ── */}
      {selectedView && !loading && (
        <>
          {/* Grand Banner */}
          <div style={{
            padding: '20px 24px', borderRadius: 12, marginBottom: 14,
            background: 'linear-gradient(135deg, rgba(0,201,167,0.06) 0%, rgba(59,130,246,0.06) 50%, rgba(139,92,246,0.06) 100%)',
            border: '1px solid rgba(0,201,167,0.20)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
              <span style={{ fontSize: 22 }}>🏟️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: THEME.textPrimary }}>{matchLabel}</div>
                <div style={{ fontSize: 13, color: THEME.textSecondary }}>
                  {clubLabel} — <span style={{ color: '#a78bfa', fontWeight: 700 }}>{grandTotal}</span> total assets
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 32, fontWeight: 800, color: '#4ade80', lineHeight: 1 }}>{pctBuilt}%</div>
                <div style={{ fontSize: 12, color: '#4ade80', fontWeight: 600 }}>Built ({grandBuilt}/{grandTotal})</div>
              </div>
            </div>
            <div style={{ height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.06)', overflow: 'hidden', position: 'relative' }}>
              <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 4, width: `${pctSent}%`, background: 'rgba(96,165,250,0.35)', transition: 'width 0.5s' }} />
              <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 4, width: `${pctBuilt}%`, background: 'linear-gradient(90deg, #34d399, #22c55e)', transition: 'width 0.5s' }} />
            </div>
            <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 13, color: THEME.textSecondary, flexWrap: 'wrap' }}>
              <span><span style={{ color: '#4ade80', fontWeight: 700 }}>{grandBuilt}</span> built & locked</span>
              <span><span style={{ color: '#60a5fa', fontWeight: 700 }}>{grandSent}</span> sent to watch folder</span>
              {grandNeedsBuild > 0 && <span><span style={{ color: '#38bdf8', fontWeight: 700 }}>{grandNeedsBuild}</span> awaiting operator build</span>}
              {grandUnchecked > 0 && <span><span style={{ color: '#fbbf24', fontWeight: 700 }}>{grandUnchecked}</span> unchecked</span>}
              {grandRejected > 0 && <span><span style={{ color: '#f87171', fontWeight: 700 }}>{grandRejected}</span> rejected</span>}
              {grandUnlocked > 0 && <span><span style={{ color: '#c084fc', fontWeight: 700 }}>{grandUnlocked}</span> club updated</span>}
            </div>
          </div>

          {/* Content Type Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 14 }}>
            {CONTENT_TYPES.map(ct => {
              const stats = data[ct.key]?.stats || computeStats([]);
              const pct = stats.total > 0 ? Math.round((stats.built / stats.total) * 100) : 0;
              const pctS = stats.total > 0 ? Math.round((stats.sent / stats.total) * 100) : 0;
              const hasProblems = stats.unchecked > 0 || stats.rejected > 0 || stats.unlocked > 0 || stats.needsBuild > 0;

              return (
                <div
                  key={ct.key}
                  onClick={() => onNavigate(ct.key)}
                  style={{
                    padding: '18px 20px',
                    borderRadius: 12,
                    background: THEME.bgCard,
                    border: `1px solid ${hasProblems ? ct.color + '30' : THEME.borderLight}`,
                    borderLeft: `4px solid ${ct.color}`,
                    cursor: 'pointer',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    display: 'flex', flexDirection: 'column',
                    minHeight: 170,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.01)'; e.currentTarget.style.boxShadow = `0 6px 24px ${ct.color}15`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 18 }}>{ct.icon}</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: THEME.textPrimary }}>{ct.shortLabel}</span>
                    </div>
                    <span style={{ fontSize: 24, fontWeight: 800, color: '#a78bfa' }}>{stats.total}</span>
                  </div>

                  {/* % built */}
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#4ade80', marginBottom: 8 }}>
                    {pct}% built ({stats.built}/{stats.total})
                  </div>

                  {/* Progress bar */}
                  <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden', position: 'relative', marginBottom: 12 }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 3, width: `${pctS}%`, background: `${ct.color}35`, transition: 'width 0.5s' }} />
                    <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 3, width: `${pct}%`, background: ct.color, transition: 'width 0.5s' }} />
                  </div>

                  {/* Stats */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 13, color: THEME.textBody, flex: 1 }}>
                    <span style={{ color: '#4ade80', fontWeight: 600 }}>✓ {stats.built} built</span>
                    {stats.needsBuild > 0 && <span style={{ color: '#38bdf8', fontWeight: 700 }}>🔨 {stats.needsBuild} needs build</span>}
                    {stats.unchecked > 0 && <span style={{ color: '#fbbf24', fontWeight: 700 }}>⚠ {stats.unchecked} unchecked</span>}
                    {stats.rejected > 0 && <span style={{ color: '#f87171', fontWeight: 700 }}>❌ {stats.rejected} rejected</span>}
                    {stats.unlocked > 0 && <span style={{ color: '#c084fc', fontWeight: 600 }}>🔓 {stats.unlocked} changed</span>}
                    {!hasProblems && stats.built === stats.total && stats.total > 0 && <span style={{ color: '#4ade80', fontWeight: 700 }}>✅ All done!</span>}
                    {stats.total === 0 && <span style={{ color: THEME.textMuted }}>No content for this match</span>}
                  </div>

                  <div style={{ fontSize: 12, color: THEME.textMuted, marginTop: 'auto', paddingTop: 10, textAlign: 'right' }}>Click to open →</div>
                </div>
              );
            })}
          </div>

          {/* Needs Attention */}
          {hasIssues && (
            <div style={{
              padding: '20px 24px', borderRadius: 12, marginBottom: 14,
              background: 'rgba(249,115,22,0.04)',
              border: '1px solid rgba(249,115,22,0.25)',
              borderLeft: '4px solid #f97316',
            }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#fb923c', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18 }}>⚡</span>
                Needs Attention
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {CONTENT_TYPES.map(ct => {
                  const s = data[ct.key]?.stats;
                  if (!s || (s.unchecked === 0 && s.rejected === 0 && s.unlocked === 0 && s.needsBuild === 0)) return null;
                  const parts = buildAttentionSentence(s);
                  return (
                    <div
                      key={ct.key}
                      onClick={() => onNavigate(ct.key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 14,
                        padding: '14px 18px', borderRadius: 10,
                        background: THEME.bgCardInner,
                        border: `1px solid ${THEME.borderLight}`,
                        cursor: 'pointer',
                        transition: 'background 0.15s, border-color 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = THEME.bgHover; e.currentTarget.style.borderColor = ct.color + '40'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = THEME.bgCardInner; e.currentTarget.style.borderColor = THEME.borderLight; }}
                    >
                      <span style={{ fontSize: 18, flexShrink: 0 }}>{ct.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: THEME.textPrimary, marginBottom: 4 }}>
                          {ct.label}
                        </div>
                        <div style={{ fontSize: 13, color: THEME.textBody, lineHeight: 1.5 }}>
                          {parts.map((part, i) => {
                            // Color the numbers
                            const match = part.match(/^(\d+)\s(.+)$/);
                            const color = part.includes('built') ? '#38bdf8'
                              : part.includes('Rejected') ? '#f87171'
                              : part.includes('QA') ? '#fbbf24'
                              : part.includes('Club') ? '#c084fc'
                              : THEME.textBody;
                            return (
                              <span key={i}>
                                {i > 0 && <span style={{ color: THEME.textMuted, margin: '0 6px' }}>·</span>}
                                {match ? (
                                  <><span style={{ color, fontWeight: 700 }}>{match[1]}</span> <span style={{ color: THEME.textBody }}>{match[2]}</span></>
                                ) : (
                                  <span style={{ color }}>{part}</span>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                      <span style={{
                        fontSize: 13, color: ct.color, fontWeight: 700,
                        flexShrink: 0, padding: '6px 14px',
                        background: `${ct.color}12`, borderRadius: 6,
                        border: `1px solid ${ct.color}30`,
                      }}>Open →</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* All clear */}
          {grandTotal > 0 && !hasIssues && (
            <div style={{
              padding: '18px 20px', borderRadius: 12,
              background: 'rgba(34,197,94,0.06)',
              border: '1px solid rgba(34,197,94,0.25)',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <span style={{ fontSize: 20 }}>✅</span>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#4ade80' }}>All content processed</div>
                <div style={{ fontSize: 14, color: THEME.textSecondary }}>{grandBuilt}/{grandTotal} built & locked for playout</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
